import { useState, useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useImageContext } from "../../contexts/ImageContext";
import { passportSuggestCrop, passportGenerate } from "../../services/api/tools/passport_photo";
import { resolveCreationImageUrl } from "../../utils/resolveCreationImageUrl";
import { resolvePassportSpec } from "../../utils/passportPresetUtils";
import logger from "../../utils/logger";
import { useEditorStore } from "../../state/editorStore";

/**
 * Auto passport processing: suggest-crop → generate (dashboard passport flow).
 * Uses the original uploaded file, not background-removed output.
 */
export function usePassportProcessing({
  enabled = false,
  passportPreset = null,
  onPassportComplete = null,
}) {
  const { selectedFile } = useImageContext();
  const { getToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [processedImage, setProcessedImage] = useState(null);
  const [creationId, setCreationId] = useState(null);
  const [error, setError] = useState(null);
  const [suggestedCrop, setSuggestedCrop] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const processingStartedRef = useRef(false);
  const processedFileRef = useRef(null);
  const hasAutoRunRef = useRef(false);
  const blobUrlRef = useRef(null);

  const revokeBlobUrl = () => {
    if (blobUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      revokeBlobUrl();
    };
  }, []);

  const applyOutput = async (token, outputUrl, rawCreationId) => {
    const { displayUrl, creationId: resolvedCid } = await resolveCreationImageUrl(
      token,
      outputUrl,
      rawCreationId
    );

    if (resolvedCid && !Number.isNaN(resolvedCid)) {
      setCreationId(resolvedCid);
      useEditorStore.getState().setLineage({
        creationId: resolvedCid,
        transparentSourceId: resolvedCid,
        rootCreationId: resolvedCid,
      });
    }

    revokeBlobUrl();
    if (displayUrl.startsWith("blob:")) {
      blobUrlRef.current = displayUrl;
    }
    setProcessedImage(displayUrl);
    if (onPassportComplete) {
      onPassportComplete(displayUrl, resolvedCid);
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!enabled || !selectedFile || !passportPreset || hasAutoRunRef.current) {
        if (!enabled || !selectedFile || !passportPreset) {
          processingStartedRef.current = false;
          processedFileRef.current = null;
        }
        return;
      }

      hasAutoRunRef.current = true;
      setIsLoading(true);
      setError(null);
      setSuggestedCrop(null);

      const spec = resolvePassportSpec(passportPreset);
      const baseOpts = {
        w_mm: spec.w_mm,
        h_mm: spec.h_mm,
        dpi: spec.dpi,
        head_h_ratio_min: spec.head_h_ratio_min,
        head_h_ratio_max: spec.head_h_ratio_max,
        eye_line_from_top: spec.eye_line_from_top,
      };

      try {
        const token = await getToken();
        if (!token) {
          throw new Error("Authentication required. Please sign in again.");
        }

        const cropData = await passportSuggestCrop(token, selectedFile, baseOpts);
        if (!cropData.success) {
          throw new Error(cropData.message || cropData.detail || "Passport suggest-crop failed.");
        }

        const crop = cropData.result || {};
        setSuggestedCrop(crop);

        const generateOpts = {
          ...baseOpts,
          bg_hex: spec.bg_hex || "#FFFFFF",
          white_background: spec.white_background !== false,
          print_sheet: false,
          output_format: "png",
        };

        if (
          crop.crop_left != null &&
          crop.crop_top != null &&
          crop.crop_w != null &&
          crop.crop_h != null
        ) {
          generateOpts.crop_left = crop.crop_left;
          generateOpts.crop_top = crop.crop_top;
          generateOpts.crop_w = crop.crop_w;
          generateOpts.crop_h = crop.crop_h;
        }

        const data = await passportGenerate(token, selectedFile, generateOpts);
        if (!data.success && !data.output_url) {
          throw new Error(data.message || data.detail || "Passport generate failed.");
        }

        const cid = data.creation_id != null ? Number(data.creation_id) : null;
        await applyOutput(token, data.output_url, cid);
        logger.info("Passport photo generated successfully");
      } catch (err) {
        logger.error("Passport processing failed", err);
        setError(err.message || "Passport processing failed. Please try again.");
        processingStartedRef.current = false;
        processedFileRef.current = null;
      } finally {
        setIsLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when file/preset/enabled/retry change
  }, [enabled, selectedFile, passportPreset?.id, retryNonce]);

  const retry = () => {
    hasAutoRunRef.current = false;
    processingStartedRef.current = false;
    processedFileRef.current = null;
    setError(null);
    setProcessedImage(null);
    setCreationId(null);
    setSuggestedCrop(null);
    revokeBlobUrl();
    setRetryNonce((n) => n + 1);
  };

  return {
    isLoading,
    processedImage,
    creationId,
    error,
    suggestedCrop,
    retry,
    setProcessedImage,
    setCreationId,
    setSuggestedCrop,
  };
}
